import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Divider, Typography } from '@material-ui/core'
import { Add } from '@material-ui/icons'
import RuleForm from './RuleForm'
import { TICKET_RULE, RULE_LABELS } from 'config/constants'

const ALL_RULE_TYPES = Object.keys(RULE_LABELS).map(Number)

const DEFAULT_RULE = (type = TICKET_RULE.BOUGHT_FROM_ANY_EVENT) => ({
  Type: type,
  RequestedEventIds: [],
  RequestedTicketIds: []
})

export const TicketRules = ({
  value,
  events,
  pendingEvents,
  onRequestTicketsOfEvent,
  onChange,
  onValidityChange,
  showAllErrors
}) => {
  const initializedRef = useRef(false)

  const getInitialRules = () => {
    if (value && value.trim() !== '') {
      try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : [parsed]
      } catch {
        return [DEFAULT_RULE()]
      }
    }
    return [DEFAULT_RULE()]
  }

  const [rules, setRules] = useState(() => {
    initializedRef.current = true
    const initial = getInitialRules()
    if (!value || value.trim() === '') {
      setTimeout(() => onChange(JSON.stringify(initial)), 0)
    }
    return initial
  })
  const [ruleValidities, setRuleValidities] = useState(() =>
    rules.map(() => true)
  )
  const [showErrors, setShowErrors] = useState(() => rules.map(() => false))
  const allRulesValid = ruleValidities.every(Boolean)
  const maxRulesReached = rules.length >= Object.keys(RULE_LABELS).length

  useEffect(() => {
    onValidityChange?.(allRulesValid)
  }, [allRulesValid, onValidityChange])

  const handleRuleChange = useCallback(
    (index, updatedRule) => {
      setRules((prev) => {
        const newRules = prev.map((r, i) => (i === index ? updatedRule : r))
        onChange(JSON.stringify(newRules))
        return newRules
      })
    },
    [onChange]
  )

  const handleAddRule = useCallback(() => {
    if (!allRulesValid) {
      setShowErrors(ruleValidities.map((v) => !v))
      return
    }
    if (maxRulesReached) return
    setRules((prev) => {
      const usedTypes = prev.map((r) => r.Type)
      const nextType = ALL_RULE_TYPES.find((t) => !usedTypes.includes(t))
      const newRules = [...prev, DEFAULT_RULE(nextType)]
      onChange(JSON.stringify(newRules))
      return newRules
    })
    setRuleValidities((prev) => [...prev, true])
    setShowErrors((prev) => [...prev, false])
  }, [onChange, allRulesValid, ruleValidities, maxRulesReached])

  const handleRemoveRule = useCallback(
    (index) => {
      setRules((prev) => {
        const newRules = prev.filter((_, i) => i !== index)
        onChange(JSON.stringify(newRules))
        return newRules
      })
      setRuleValidities((prev) => prev.filter((_, i) => i !== index))
      setShowErrors((prev) => prev.filter((_, i) => i !== index))
    },
    [onChange]
  )

  const handleValidChange = useCallback((index, isValid) => {
    setRuleValidities((prev) => {
      const next = [...prev]
      next[index] = isValid
      return next
    })
  }, [])

  return (
    <div>
      {rules.map((rule, index) => (
        <React.Fragment key={index}>
          {index > 0 && <Divider style={{ margin: '8px 0' }} />}
          <Typography
            variant='caption'
            style={{ marginLeft: 8, color: '#666' }}>
            Regla {index + 1}
          </Typography>
          <RuleForm
            rule={rule}
            events={events}
            pendingEvents={pendingEvents}
            onRequestTicketsOfEvent={onRequestTicketsOfEvent}
            onChange={(updatedRule) => handleRuleChange(index, updatedRule)}
            onValidChange={(isValid) => handleValidChange(index, isValid)}
            usedTypes={rules.filter((_, i) => i !== index).map((r) => r.Type)}
            showError={
              showAllErrors
                ? !ruleValidities[index]
                : showErrors[index] || false
            }
            onRemove={rules.length > 1 ? () => handleRemoveRule(index) : null}
          />
        </React.Fragment>
      ))}
      <Button
        startIcon={<Add />}
        onClick={handleAddRule}
        size='small'
        disabled={maxRulesReached}
        style={{ marginLeft: 8, marginTop: 4 }}>
        Agregar Regla
      </Button>
    </div>
  )
}

export default TicketRules
